/** Shared primitives. Styling lives in index.css; these own the semantics. */
import type { ReactNode } from 'react';
import { CircleNotch, Info, Tray, WarningCircle, X } from '@phosphor-icons/react';
import { initials as toInitials } from '../lib/format';

export type Tone = 'ok' | 'info' | 'warn' | 'bad' | 'off';

export const TONE_COLOR: Record<Tone, string> = {
  ok: 'var(--ok)',
  info: 'var(--info)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  off: 'var(--off)',
};

export function Badge({ tone = 'off', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge tone-${tone}`}>{children}</span>;
}

export function StatusDot({ tone }: { tone: Tone }) {
  return <span className="dot" style={{ background: TONE_COLOR[tone] }} />;
}

export function Avatar({ name, size = 38 }: { name?: string; size?: number }) {
  return (
    <div
      className="icon-tile"
      style={{
        width: size,
        height: size,
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
        fontSize: size * 0.36,
        fontWeight: 600,
      }}
    >
      {toInitials(name)}
    </div>
  );
}

export function Spinner({ large, onAccent }: { large?: boolean; onAccent?: boolean }) {
  return (
    <span
      className={`spinner${large ? ' spinner-lg' : ''}${onAccent ? ' spinner-on-accent' : ''}`}
      role="status"
      aria-label="Loading"
    />
  );
}

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
        <Spinner large />
      </div>
      <p className="t-sm muted" style={{ margin: 0 }}>
        {label}
      </p>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  message,
  action,
}: {
  icon?: ReactNode;
  title: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-mark">{icon ?? <Tray size={24} />}</div>
      <h3 style={{ fontSize: 15, marginBottom: 5 }}>{title}</h3>
      {message && (
        <p className="t-sm muted" style={{ margin: '0 auto', maxWidth: 380, lineHeight: 1.6 }}>
          {message}
        </p>
      )}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<WarningCircle size={24} color="var(--bad)" />}
      title="Could not load this"
      message={message}
      action={
        onRetry && (
          <button className="btn" onClick={onRetry}>
            Try again
          </button>
        )
      }
    />
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="field-label">
        {label}
        {required && ' *'}
      </label>
      {children}
      {error ? <p className="field-error">{error}</p> : hint ? <p className="field-hint">{hint}</p> : null}
    </div>
  );
}

/** A dashed callout for screens whose data the backend does not serve yet. */
export function PreviewNote({ children }: { children: ReactNode }) {
  return (
    <div className="preview-note">
      <Info size={17} color="var(--mute)" style={{ flex: 'none', marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 17 }}>{title}</h2>
            {subtitle && (
              <p className="t-sm muted" style={{ margin: '3px 0 0' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button className="btn btn-icon" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="drawer-body scroll">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </aside>
    </>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="modal">
        <div className="modal-card" role="dialog" aria-modal="true" aria-label={title}>
          <div style={{ padding: '20px 22px 0' }}>
            <h2 style={{ fontSize: 17 }}>{title}</h2>
          </div>
          <div style={{ padding: '14px 22px 20px' }}>{children}</div>
          {footer && (
            <div
              style={{
                padding: '14px 22px',
                borderTop: '1px solid var(--line-3)',
                display: 'flex',
                gap: 9,
                justifyContent: 'flex-end',
                background: 'var(--inset)',
                borderRadius: '0 0 18px 18px',
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function DefinitionList({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="dl">
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'contents' }}>
          <dt>{label}</dt>
          <dd>{value || <span className="faint">Not set</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { id: T; label: string; count?: number }[];
  onChange: (id: T) => void;
}) {
  return (
    <div className="seg">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={option.id === value ? 'is-active' : undefined}
          onClick={() => onChange(option.id)}
        >
          {option.label}
          {option.count !== undefined && ` ${option.count}`}
        </button>
      ))}
    </div>
  );
}

export function SubmitButton({
  pending,
  children,
  pendingLabel = 'Working…',
  ...rest
}: {
  pending: boolean;
  children: ReactNode;
  pendingLabel?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} className={rest.className ?? 'btn btn-primary'} disabled={pending || rest.disabled}>
      {pending ? (
        <>
          <Spinner onAccent /> {pendingLabel}
        </>
      ) : (
        children
      )}
    </button>
  );
}

export { CircleNotch };
